/**
 * Page liste / catalogue garanties — `/guarantees` (refonte 9.P.3.C).
 *
 * Pattern UX commun :
 *   - sticky page header `top-16 lg:top-0` + filter bar
 *   - catalogue STATIQUE depuis `@kollectivia/shared/constants` (GUARANTEES)
 *   - groupes affichés en CardSection moderne par groupe
 *   - search + group filter local
 *   - states : empty / no-match
 *   - max-w-screen-2xl
 *
 * D5 — Si à terme catalogue dynamique par tenant, créer `app.guarantees` +
 * RLS + endpoints CRUD. Hors scope MVP.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { GUARANTEES, type GuaranteeGroup } from "@kollectivia/shared/constants";

import { SearchInput } from "../../components/SearchInput";

const GROUPS: (GuaranteeGroup | "all")[] = [
  "all",
  "vie",
  "amc",
  "dentaire",
  "vue",
  "invalidite",
  "bien_etre",
];

export function GuaranteeListPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";

  const [q, setQ] = useState("");
  const [group, setGroup] = useState<(typeof GROUPS)[number]>("all");

  // Filtrage local + groupement par `group`. Ordre déclaratif GUARANTEES préservé.
  const groupedFiltered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const filtered = GUARANTEES.filter((g) => {
      if (group !== "all" && g.group !== group) return false;
      if (!term) return true;
      const label = (lang === "en" ? g.label_en : g.label_fr).toLowerCase();
      return label.includes(term) || g.code.includes(term);
    });
    const byGroup = new Map<GuaranteeGroup, typeof GUARANTEES[number][]>();
    for (const g of filtered) {
      const arr = byGroup.get(g.group) ?? [];
      arr.push(g);
      byGroup.set(g.group, arr);
    }
    return byGroup;
  }, [q, group, lang]);

  const totalShown = useMemo(() => {
    let n = 0;
    for (const arr of groupedFiltered.values()) n += arr.length;
    return n;
  }, [groupedFiltered]);

  const hasFilters = q.trim() !== "" || group !== "all";

  const onResetFilters = () => {
    setQ("");
    setGroup("all");
  };

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      {/* Sticky page header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("guarantees.list.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("guarantees.list.subtitle")}</p>
      </header>

      <div className="px-0 py-6 lg:py-8">
        {/* Filters bar */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="min-w-[16rem] flex-1">
            <SearchInput
              value={q}
              onChange={setQ}
              placeholder={t("guarantees.list.search_placeholder")}
            />
          </div>
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t("guarantees.list.filters.group")}</span>
            <select
              className="input"
              value={group}
              onChange={(e) => setGroup(e.target.value as (typeof GROUPS)[number])}
            >
              {GROUPS.map((g) => (
                <option key={g} value={g}>
                  {g === "all"
                    ? t("guarantees.list.groups_all")
                    : t(`projects.guarantees.groups.${g}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {totalShown === 0 ? (
          hasFilters ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <strong className="block font-semibold">
                    {t("guarantees.list.no_match.title")}
                  </strong>
                  <span className="text-amber-700">
                    {t("guarantees.list.no_match.subtitle")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onResetFilters}
                  className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                >
                  {t("guarantees.list.no_match.reset")}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
              <h2 className="text-base font-semibold text-slate-900">
                {t("guarantees.list.empty")}
              </h2>
            </div>
          )
        ) : (
          <div className="space-y-5">
            {Array.from(groupedFiltered.entries()).map(([groupKey, items]) => (
              <section
                key={groupKey}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
              >
                <header className="border-b border-slate-100 bg-slate-50 px-5 py-3.5">
                  <h2 className="text-base font-semibold text-slate-900">
                    {t(`projects.guarantees.groups.${groupKey}`)}
                  </h2>
                </header>
                <ul className="divide-y divide-slate-100">
                  {items.map((g) => (
                    <li key={g.code}>
                      <Link
                        to={`/guarantees/${g.code}`}
                        className="flex items-center justify-between px-5 py-3 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
                      >
                        <span className="font-medium text-slate-800">
                          {lang === "en" ? g.label_en : g.label_fr}
                        </span>
                        <span className="font-mono text-xs text-slate-400">
                          {g.code}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
